import {RemoteConstantsModel, TechTreeComponent} from "../models/remoteConstants.model";
import {cellKey, MIN_CELL_GAP} from "./graphCanvas";

// Model-level helpers for authored tech tree layout. Kept out of the canvas so
// commands (which never see React) can share them.

// The tree's implicit anchor. Every node hangs off it, it carries no localized
// name, and it can't be deleted or given a parent. The Tech Tree canvas doesn't
// draw it at all — it exists to root the hierarchy and to seed the computed
// fallback layout — so the tab's Node Details pane is the only place it's
// visible. Matched by id rather than by being techTree[0] because that's what
// the rest of the tab does; AddTechDependencyCommand is the one place that
// still keys off index 0.
export const ROOT_ID = 'root';

// A free grid cell for a new child of `parent`, or undefined when the parent
// itself has no authored position — in that case the child is left unplaced too
// so both keep falling through to the computed layout, rather than stranding a
// placed child next to a computed parent.
export function freeCellNearParent(
    techTree: TechTreeComponent[],
    parentId: string,
): {x: number; y: number} | undefined {
    const parent = techTree.find(t => t.id === parentId);
    if (!parent || !Number.isInteger(parent.x) || !Number.isInteger(parent.y)) return undefined;

    const taken = new Set<string>();
    for (const tech of techTree) {
        if (Number.isInteger(tech.x) && Number.isInteger(tech.y))
            taken.add(cellKey(tech.x as number, tech.y as number));
    }

    // Children sit to the right of their parent, fanning out vertically before
    // stepping further right. Offsets are multiples of MIN_CELL_GAP so the new
    // node doesn't land overlapping its parent — the grid is finer than a node
    // circle, so a one-cell step would put them on top of each other.
    //
    // Bounded: past this the node stays unplaced rather than landing somewhere
    // arbitrary and far from the tree.
    const px = parent.x as number;
    const py = parent.y as number;
    for (let step = 1; step <= 4; step++) {
        for (const lane of [0, 1, -1, 2, -2, 3, -3]) {
            const x = px + step * MIN_CELL_GAP;
            const y = py + lane * MIN_CELL_GAP;
            if (!taken.has(cellKey(x, y))) return {x, y};
        }
    }
    return undefined;
}

// Drops the authored route for one edge. Called when the dependency itself goes
// away, so a re-added edge starts straight again instead of resurrecting bends
// the designer can no longer see.
export function removeEdgeRoute(node: TechTreeComponent, childId: string): void {
    if (!node.routes) return;
    node.routes = node.routes.filter(r => r.child !== childId);
    if (node.routes.length === 0) delete node.routes;
}

// Drops every route pointing at a node that no longer exists, across the whole
// tree. Without this, deleting a node leaves orphaned route records behind.
export function pruneRoutesTo(data: RemoteConstantsModel, removedId: string): void {
    for (const tech of data.techTree) {
        if (tech.routes) removeEdgeRoute(tech, removedId);
    }
}

// Every node that must go when `removedId` is deleted: the node itself, plus each
// node that was reachable before and is no longer reachable once it's gone — i.e.
// descendants whose only path into the tree ran through it. A descendant that any
// other parent still reaches is KEPT: the tech tree is a DAG, not a tree, so
// deleting one parent of a shared node must not take the node with it.
//
// Reachability is measured from the graph's entry points: the root anchor plus
// every node that already has no parent. Including the pre-existing orphans (the
// canvas draws them in their own column) matters both ways round — deleting one
// takes its subtree with it, and deleting anything else leaves the others alone
// instead of sweeping them up as collateral.
export function collectTechRemovals(techTree: TechTreeComponent[], removedId: string): Set<string> {
    const childrenOf = new Map<string, string[]>();
    const hasParent = new Set<string>();
    for (const tech of techTree) {
        childrenOf.set(tech.id, tech.children ?? []);
        for (const child of tech.children ?? []) hasParent.add(child);
    }

    const sources = techTree
        .map(t => t.id)
        .filter(id => id === ROOT_ID || !hasParent.has(id));

    // Nodes reachable from the entry points, optionally with one node cut out.
    // `seen` doubles as the cycle guard, so malformed data can't spin here.
    function reachable(without?: string): Set<string> {
        const seen = new Set<string>();
        const stack = sources.filter(id => id !== without);
        while (stack.length > 0) {
            const id = stack.pop() as string;
            if (id === without || seen.has(id)) continue;
            seen.add(id);
            for (const child of childrenOf.get(id) ?? []) stack.push(child);
        }
        return seen;
    }

    const before = reachable();
    const after = reachable(removedId);

    // Seeded with the target explicitly: a node inside a detached cycle isn't in
    // `before`, so the set difference alone would never name it.
    const removals = new Set<string>([removedId]);
    for (const id of Array.from(before)) {
        if (!after.has(id)) removals.add(id);
    }
    removals.delete(ROOT_ID);   // the anchor is never removed
    return removals;
}
